import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { ErrorModal } from "@dexteel/mesf-core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { Roll } from "../../models/Roll";
import { getReadyRolls } from "../../repositories/RollRepository";
import { EditRollModal } from "./EditRollModal";
import { MaintenanceRollModal } from "./MaintenanceRollModal";
import { RollCard } from "./RollCard";
import { RollSelectorDialog } from "./RollSelectorDialog";

type useRollsProps = {
	sectionId?: number | null | undefined;
	standId?: number | null | undefined;
	onError: (error: Error) => void;
	onSuccess?: (data: Roll[]) => void;
	enabled?: boolean;
};

export const useRolls = ({
	sectionId,
	standId,
	onError,
	onSuccess,
	enabled = true,
}: useRollsProps) => {
	return useQuery<Roll[], Error>({
		queryKey: ["rolls", sectionId, standId],
		meta: { realtime: ["M2.GetReadyRolls"] },
		queryFn: ({ signal }) =>
			getReadyRolls(
				{
					sectionId,
					standId,
				},
				signal,
			),
		onError: onError,
		onSuccess: onSuccess,
		enabled: enabled,
	});
};

type Props = {
	label: string;
	value: number | null;
	onChange?: (
		id: number | null,
		diameter?: number | null,
		standId?: number | null,
		type?: string | null,
	) => void;
	onDiameterChange?: (diameter: number | null) => void;
	onStandChange?: (standId: number | null) => void;
	standId?: number | null;
	sectionId?: number | null;
	diameter?: number | null;
	diameterError?: string;
	disabled?: boolean;
	fieldError?: FieldError | undefined;
	filterRolls?: "T" | "B" | "A";
	onForceAssembly?: (force: boolean) => void;
	params?: {
		sectionId?: number | null | undefined;
		standId?: number | null | undefined;
	};
};

export const RollSelector = ({
	label,
	value,
	onChange = () => {},
	onDiameterChange = () => {},
	standId,
	sectionId,
	diameter,
	diameterError,
	disabled = false,
	fieldError,
	filterRolls,
	onForceAssembly = undefined,
	params = { sectionId: undefined, standId: undefined },
}: Props) => {
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [selectedValue, setSelectedValue] = useState<Roll | null>(null);
	const [openEditModal, setOpenEditModal] = useState(false);
	const [openMaintenanceModal, setOpenMaintenanceModal] = useState(false);
	const [openDialog, setOpenDialog] = useState(false);

	const { data: rolls, isLoading } = useRolls({
		sectionId: params.sectionId,
		standId: params.standId,
		onError: (error) => setError(error.message),
	});

	const selectedRoll = rolls?.find((roll) => roll.PartId === value) || null;

	const handleConfirmationClose = (result?: string) => {
		setMessage("");
		if (result === "OK") {
			onForceAssembly ? onForceAssembly(true) : null;
			onChange(
				selectedValue?.PartId ?? null,
				selectedValue?.Diameter ?? null,
				selectedValue?.StandId ?? null,
				selectedValue?.RollTypeCode,
			);
			setSelectedValue(null);
			return;
		}
	};

	const handleRollSelect = (roll: Roll | null) => {
		if (roll) {
			onChange(roll.PartId, roll.Diameter, roll.StandId, roll.RollTypeCode);
		}
	};

	const handleClear = () => {
		onChange(null);
	};

	return (
		<>
			<div data-test-id="roll-selector">
				<RollCard
					label={label}
					roll={selectedRoll}
					isLoading={isLoading}
					error={fieldError?.message}
					disabled={disabled}
					sectionId={sectionId}
					standId={standId}
					diameter={diameter}
					diameterError={diameterError}
					onDiameterChange={onDiameterChange}
					onClick={() => setOpenDialog(true)}
					onMaintenanceClick={() => setOpenMaintenanceModal(true)}
					onSetReadyClick={() => setOpenEditModal(true)}
					onClear={selectedRoll ? handleClear : undefined}
				/>
			</div>

			<RollSelectorDialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				onSelect={handleRollSelect}
				rolls={rolls || []}
				isLoading={isLoading}
				title={label.toUpperCase()}
				filterRolls={filterRolls}
				selectedRollId={value}
				standId={standId ?? undefined}
				sectionId={sectionId ?? undefined}
			/>

			<EditRollModal
				show={openEditModal}
				onHide={(newDiameter?: number) => {
					setOpenEditModal(false);
					if (newDiameter !== undefined) {
						onDiameterChange(newDiameter);
					}
				}}
				roll={selectedRoll!}
			/>
			<MaintenanceRollModal
				show={openMaintenanceModal}
				onHide={(newDiameter?: number) => {
					setOpenMaintenanceModal(false);
					if (newDiameter !== undefined) {
						onDiameterChange(newDiameter);
					}
				}}
				rollId={selectedRoll?.PartId!}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading rolls"
			/>
			<ConfirmationDialogRaw
				title="CONFIRMATION"
				message={message}
				open={message !== ""}
				onClose={handleConfirmationClose}
			/>
		</>
	);
};
